import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { ExternalLink } from "src/base-components/ExternalLink";
import { useCaseInsightsAccess } from "src/cases/analytics/useCaseInsightsAccess";
import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { DOCS_CASE_ANALYTICS } from "src/constants/ExternalLinks";
import { Button } from "src/design-system/Button";
import { Modal } from "src/design-system/Modal";
import { useCapabilities } from "src/hooks/useCapabilities";
import {
  DashboardPageParams,
  getUrlToCaseAnalyticsPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type IntroModalProps = {
  onClose: () => void;
  onConfirm: () => void;
};

const DocsHint: React.FC = () => (
  <>
    To see what you can ask,{" "}
    <ExternalLink className="underline" href={DOCS_CASE_ANALYTICS}>
      read the documentation
    </ExternalLink>
    .
  </>
);

const ConfirmButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <Button
    dataLoc="case-insights-intro-confirm"
    variant="primary"
    onClick={onClick}
  >
    {children}
  </Button>
);

const AdminIntroModal: React.FC<IntroModalProps> = ({ onClose, onConfirm }) => (
  <Modal open onClose={onClose}>
    <Modal.Header
      description={
        <div className="flex flex-col gap-y-3">
          <p>
            To understand how your Cases are moving, ask a question in plain
            language and Case Insights builds the chart for you.
          </p>
          <p>
            Track volumes, workload, and turnaround time. Pin the charts you
            want to keep to a dashboard, or build one dashboard per team.
          </p>
          <p>
            You&apos;ll find it in the new sidebar on the left, alongside your
            queues and case templates. <DocsHint />
          </p>
        </div>
      }
    >
      Case Insights is now part of Case Manager! 🎉
    </Modal.Header>
    <Modal.Footer
      primaryButton={
        <ConfirmButton onClick={onConfirm}>Explore Case Insights</ConfirmButton>
      }
      secondaryButton={false}
    />
  </Modal>
);

const InvitedEditorIntroModal: React.FC<
  IntroModalProps & { dashboardName: string }
> = ({ dashboardName, onClose, onConfirm }) => (
  <Modal open onClose={onClose}>
    <Modal.Header
      description={
        <div className="flex flex-col gap-y-3">
          <p>
            You are invited to the {dashboardName} dashboard, so Case Insights
            is now available in your sidebar. Use it to see how your Cases are
            moving, track volumes, workload, and turnaround time.
          </p>
          <p>
            <DocsHint />
          </p>
        </div>
      }
    >
      You now have access to Case Insights
    </Modal.Header>
    <Modal.Footer
      primaryButton={
        <ConfirmButton onClick={onConfirm}>View dashboard</ConfirmButton>
      }
      secondaryButton={false}
    />
  </Modal>
);

export const CaseInsightsIntroModal: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const { canAccess, invitedDashboardName } = useCaseInsightsAccess();
  const { workspaceRole } = useCapabilities();
  const [introSeen, setIntroSeen] = useLocalStorage(
    `caseInsightsIntroSeen:${wsId}`,
    false,
  );

  if (!canAccess || introSeen) {
    return null;
  }

  const handleClose = () => setIntroSeen(true);
  const handleConfirm = () => {
    setIntroSeen(true);
    navigate(getUrlToCaseAnalyticsPage(orgId, wsId));
  };

  if (WorkspaceSimpleRoleType.ADMIN === workspaceRole) {
    return <AdminIntroModal onClose={handleClose} onConfirm={handleConfirm} />;
  }

  if (!invitedDashboardName) {
    return null;
  }

  return (
    <InvitedEditorIntroModal
      dashboardName={invitedDashboardName}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  );
};
